import React from 'react';
import { DiscountListItem, DiscountListItemStatus } from 'app/pages/DiscountsList/modules/DiscountItemsList/state';
import { DiscountStatus, DiscountType } from 'app/shared/model/DiscountType';
import '../Page.css';

import { Card, Tag, Popover, Typography, theme } from 'antd';
import { ListLayout, ListLayoutItem } from 'app/shared/ui/ListLayout';
import { 
    GlobalOutlined, 
    UserOutlined, 
    ShopOutlined,
    TransactionOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    TagOutlined,
    LockOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { useToken } = theme;

interface DiscountItemProps {
    discount: DiscountListItem;
}

const DiscountItem: React.FC<DiscountItemProps> = ({ discount }) => {
    const { token } = useToken();
    const statusConfig = getStatusConfig(discount.discountStatus.status);
    const entityLocked = discount.entityUpdate === true;

    return (
        <Card
            id={discount.id.toString()}
            style={{ width: '100%', marginBottom: token.margin }}
            styles={{
                body: { padding: `${token.paddingSM}px ${token.padding}px` },
                header: { padding: token.paddingSM }
            }}
            className={entityLocked ? "discount-locked" : ""}
        >
            {entityLocked && <div className="discount-locked-icon">
                <LockOutlined />
                <p>Locked due to pending changes</p>
            </div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: token.marginXS }}>
                <Text strong>
                    <a style={{fontSize:17 , fontWeight:'bold'}} className={entityLocked ? "link-disabled" : ""} href={`discount?id=${discount.id.toString()}`}>
                        {discount.internalName}
                    </a>
                </Text>
                <Tag
                    color={statusConfig.color}
                    icon={statusConfig.icon}
                    style={{ margin: 0 }}
                >
                    {discount.discountStatus.description}
                </Tag>
            </div>

            <Text type="secondary" style={{ display: 'block', marginBottom: token.margin }}>
                Public name: {discount.publicName}
            </Text>

            <div style={{ marginBottom: token.marginXS }}>
                <div style={{ marginBottom: token.marginXS }}>
                    {renderLabelList(discount.countries.map(x => x.name), 5, "All countries", <GlobalOutlined />)}
                </div>

                <div style={{ marginBottom: token.marginXS }}>
                    {renderLabelList(discount.agents.map(x => x.name), 3, "All agents", <UserOutlined />)}
                </div>

                {discount.currencies.length > 0 && (
                    <div style={{ marginBottom: token.marginXS }}>
                        {renderLabelList(discount.currencies, 5, "", <TransactionOutlined />)}
                    </div>
                )}

                <div>
                    {renderLabelList(discount.schools.map(x => x.name), 7, "All schools", <ShopOutlined />)}
                </div>
            </div>
            {renderTypeTag(discount.discountType)}
        </Card>
    );
};

function renderLabelList(items: string[], maxVisible: number, allText: string, icon: React.ReactNode) {
    if (items.length === 0) {
        return (
            <Tag icon={icon} style={{ marginRight: 8, marginBottom: 8 }}>
                {allText}
            </Tag>
        );
    }

    const visibleItems = items.slice(0, maxVisible);
    const hiddenItems = items.slice(maxVisible);

    return (
        <>
            {visibleItems.map(item => (
                <Tag icon={icon} key={item} style={{ marginRight: 8, marginBottom: 8 }}>
                    {item}
                </Tag>
            ))}
            {hiddenItems.length > 0 && (
                <Popover
                    title="Additional Items"
                    content={
                        <ListLayout size="small">
                            {hiddenItems.map((item) => (
                                <ListLayoutItem key={item}>{item}</ListLayoutItem>
                            ))}
                        </ListLayout>
                    }
                >
                    <Tag icon={icon} style={{ marginRight: 8, marginBottom: 8 }}>
                        +{hiddenItems.length} more
                    </Tag>
                </Popover>
            )}
        </>
    );
}

function renderTypeTag(discountType: DiscountType) {
    switch(discountType) {
        case DiscountType.marketDiscount:
            return (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tag icon={<TagOutlined />} color="blue" style={{ margin: 0 }}>
                        Market Discount
                    </Tag>
                </div>
            );
        case DiscountType.specialOffer:
            return (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tag icon={<TagOutlined />} color="purple" style={{ margin: 0 }}>
                        Special Offer
                    </Tag>
                </div>
            );
        default:
            return null;
    }
}

interface StatusConfig {
    color: string;
    icon: React.ReactNode;
}

function getStatusConfig(status: DiscountStatus): StatusConfig {
    switch(status) {
        case DiscountStatus.Active:
            return { color: 'success', icon: <CheckCircleOutlined /> };
        case DiscountStatus.ExpiresInLessThan7Days:
            return { color: 'warning', icon: <ClockCircleOutlined /> };
        case DiscountStatus.FutureStart:
            return { color: 'processing', icon: <ClockCircleOutlined /> };
        case DiscountStatus.RequiresApproval:
            return { color: 'gold', icon: <ExclamationCircleOutlined /> };
        default:
            return { color: 'error', icon: <CloseCircleOutlined /> };
    }
}

export default DiscountItem;